
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import Section from '../ui/Section';
import Container from '../ui/Container';

export default function About() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const workflowSteps = [
    { 
      name: t('about.workflow.discovery'), 
      icon: 'ri-search-2-line'
    },
    { 
      name: t('about.workflow.design'), 
      icon: 'ri-palette-line'
    },
    { 
      name: t('about.workflow.develop'), 
      icon: 'ri-code-s-slash-line'
    },
    { 
      name: t('about.workflow.deploy'), 
      icon: 'ri-rocket-line'
    }
  ];

  const values = [
    {
      title: t('about.innovation.title'),
      description: t('about.innovation.description'),
      icon: 'ri-lightbulb-line'
    },
    {
      title: t('about.trust.title'),
      description: t('about.trust.description'),
      icon: 'ri-shield-check-line'
    },
    {
      title: t('about.modernTech.title'),
      description: t('about.modernTech.description'),
      icon: 'ri-smartphone-line'
    }
  ];

  return (
    <Section ref={sectionRef} id="about" className="py-20 bg-gradient-to-b from-blue-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=&quot;60&quot; height=&quot;60&quot; viewBox=&quot;0 0 60 60&quot; xmlns=&quot;http://www.w3.org/2000/svg&quot;%3E%3Cg fill=&quot;none&quot; fill-rule=&quot;evenodd&quot;%3E%3Cg fill=&quot;%232563eb&quot; fill-opacity=&quot;0.02&quot;%3E%3Ccircle cx=&quot;30&quot; cy=&quot;30&quot; r=&quot;2&quot;/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>
      
      {/* Floating elements with blur effects */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/20 backdrop-blur-sm rounded-full animate-pulse"></div>
      <div className="absolute top-40 right-16 w-24 h-24 bg-blue-300/15 backdrop-blur-sm rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute bottom-32 left-20 w-20 h-20 bg-blue-400/10 backdrop-blur-sm rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="absolute bottom-20 right-32 w-28 h-28 bg-blue-500/15 backdrop-blur-sm rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      
      <Container className="relative z-10">
        {/* Header Section - Same style as other sections with animation */}
        <div className={`text-center mb-16 transition-all duration-1000 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6 font-poppins">
            {t('about.title')}
          </h2>
          <p className="text-xl text-blue-700 max-w-3xl mx-auto font-inter">
            {t('about.description')}
          </p>
        </div>
        
        {/* Workflow Process - Enhanced with blur effects */}
        <div className={`mb-20 transition-all duration-1200 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`} style={{ transitionDelay: '200ms' }}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {workflowSteps.map((step, index) => (
              <div key={step.name} className={`text-center group transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`} style={{ transitionDelay: `${400 + index * 150}ms` }}>
                <div className="relative">
                  <div className="w-16 h-16 bg-blue-100/80 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto group-hover:bg-blue-200/80 group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-xl border border-blue-200/50">
                    <i className={`${step.icon} text-2xl text-blue-600 group-hover:scale-110 transition-transform duration-300`}></i>
                  </div>
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 w-16 h-16 bg-blue-400/20 rounded-full mx-auto opacity-0 group-hover:opacity-100 group-hover:scale-125 transition-all duration-300 blur-md"></div>
                </div>
                <h3 className="text-lg font-semibold text-blue-900 font-poppins group-hover:text-blue-700 transition-colors duration-300">
                  {step.name}
                </h3>
              </div>
            ))}
          </div>
        </div>

        {/* Satisfaction Section - Enhanced with blur and glow effects */}
        <div className={`text-center mb-20 transition-all duration-1000 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`} style={{ transitionDelay: '600ms' }}>
          <div className="relative inline-block group">
            <div className="w-20 h-20 bg-blue-100/80 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300 border border-blue-200/50">
              <i className="ri-award-line text-3xl text-blue-600 group-hover:scale-110 transition-transform duration-300"></i>
            </div>
            {/* Animated glow ring */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-20 h-20 bg-blue-400/20 rounded-full opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500 blur-lg"></div>
          </div>
          
          <div className="text-6xl font-bold text-blue-900 mb-4 font-poppins group hover:scale-105 transition-transform duration-300 cursor-default">
            100%
          </div>
          
          <h3 className="text-2xl font-bold text-blue-900 mb-4 font-poppins">
            {t('about.satisfaction')}
          </h3>
          
          <p className="text-lg text-blue-700 font-inter mb-8 max-w-2xl mx-auto">
            {t('about.excellence')}
          </p>
          
          {/* Achievement badges - Enhanced with blur effects */}
          <div className="flex flex-wrap justify-center gap-6 max-w-2xl mx-auto">
            <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 border border-white/80 group">
              <i className="ri-check-line text-green-600 group-hover:scale-110 transition-transform duration-300"></i>
              <span className="text-blue-700 font-medium">{t('about.quality')}</span>
            </div>
            
            <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 border border-white/80 group">
              <i className="ri-time-line text-blue-600 group-hover:scale-110 transition-transform duration-300"></i>
              <span className="text-blue-700 font-medium">{t('about.delivery')}</span>
            </div>
            
            <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 border border-white/80 group">
              <i className="ri-customer-service-2-line text-purple-600 group-hover:scale-110 transition-transform duration-300"></i>
              <span className="text-blue-700 font-medium">{t('about.support')}</span>
            </div>
          </div>
        </div>

        {/* Values Section - Enhanced with staggered animations and blur effects */}
        <div className="grid md:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div key={value.title} className={`text-center group transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
            }`} style={{ transitionDelay: `${800 + index * 200}ms` }}>
              <div className="relative">
                <div className="w-16 h-16 bg-blue-100/80 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-blue-200/80 group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-xl border border-blue-200/50">
                  <i className={`${value.icon} text-2xl text-blue-600 group-hover:scale-110 transition-transform duration-300`}></i>
                </div>
                {/* Animated background glow */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-blue-400/20 rounded-full opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500 blur-lg"></div>
              </div>
              
              <h3 className="text-xl font-bold text-blue-900 mb-4 font-poppins group-hover:text-blue-700 transition-colors duration-300">
                {value.title}
              </h3>
              
              <p className="text-blue-700 font-inter group-hover:text-blue-600 transition-colors duration-300">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}
